import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  Calendar,
  Clock,
  ArrowRight,
  Search,
  TrendingUp,
  BookOpen,
  Sparkles,
  Users,
  Bell,
  CheckCircle2
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { BlogBackground } from '@/components/ui/BlogBackground';

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  author: {
    name: string;
    avatar: string;
    role: string;
  };
  date: string;
  readTime: string;
  category: string;
  featured?: boolean;
}

const categories = [
  { id: 'all', label: 'All Posts', icon: BookOpen },
  { id: 'product', label: 'Product', icon: Sparkles },
  { id: 'engineering', label: 'Engineering', icon: TrendingUp },
  { id: 'tips', label: 'Tips & Tricks', icon: BookOpen },
  { id: 'enterprise', label: 'Enterprise', icon: Users },
];

const blogPosts: BlogPost[] = [
  {
    slug: 'ochtopus-ai-agent-2-release',
    title: 'Ochtopus AI Agent 2.0: Your New Coding Partner',
    excerpt: 'We\'re excited to announce the next generation of Ochtopus AI Agent, featuring improved context understanding, multi-file refactoring, and autonomous task execution.',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=600&fit=crop',
    author: { name: 'Ochtopus Team', avatar: 'OT', role: 'Product Team' },
    date: 'Dec 20, 2025',
    readTime: '5 min',
    category: 'product',
    featured: true
  },
  {
    slug: 'ochtopus-built-in-testing',
    title: 'Introducing Built-in Unit & UAT Testing in Ochtopus',
    excerpt: 'Run tests directly in your IDE without external tools. Ochtopus now includes a complete testing framework with AI-powered test generation.',
    image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=1200&h=600&fit=crop',
    author: { name: 'Ochtopus Team', avatar: 'OT', role: 'Engineering Team' },
    date: 'Dec 18, 2025',
    readTime: '6 min',
    category: 'product'
  },
  {
    slug: 'ochtopus-keyboard-shortcuts',
    title: '10 Ochtopus Keyboard Shortcuts That Will 10x Your Productivity',
    excerpt: 'Master these essential keyboard shortcuts in Ochtopus and watch your coding speed skyrocket. From navigation to AI commands.',
    image: 'https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=1200&h=600&fit=crop',
    author: { name: 'Ochtopus Team', avatar: 'OT', role: 'Developer Relations' },
    date: 'Dec 15, 2025',
    readTime: '4 min',
    category: 'tips'
  },
  {
    slug: 'ochtopus-enterprise-security',
    title: 'Building Secure Applications with Ochtopus Enterprise',
    excerpt: 'Learn how enterprise teams are using Ochtopus to build secure, compliant applications while maintaining developer productivity.',
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1200&h=600&fit=crop',
    author: { name: 'Ochtopus Team', avatar: 'OT', role: 'Enterprise Team' },
    date: 'Dec 10, 2025',
    readTime: '6 min',
    category: 'enterprise'
  },
  {
    slug: 'ochtopus-vs-vscode-migration',
    title: 'From VS Code to Ochtopus: Complete Migration Guide',
    excerpt: 'Everything you need to know about migrating from VS Code to Ochtopus. Import your settings, extensions, and keybindings with one click.',
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1200&h=600&fit=crop',
    author: { name: 'Ochtopus Team', avatar: 'OT', role: 'Technical Writing' },
    date: 'Dec 5, 2025',
    readTime: '7 min',
    category: 'tips'
  },
  {
    slug: 'ochtopus-ai-native-development',
    title: 'The Future of Code: AI-Native Development with Ochtopus',
    excerpt: 'What does the future of software development look like? We explore AI-native development and how Ochtopus is leading the way.',
    image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1200&h=600&fit=crop',
    author: { name: 'Ochtopus Team', avatar: 'OT', role: 'Leadership' },
    date: 'Nov 28, 2025',
    readTime: '10 min',
    category: 'engineering'
  },
  {
    slug: 'ochtopus-claude-opus-integration',
    title: 'Ochtopus + Claude Opus 4.5: Advanced AI Reasoning',
    excerpt: 'How we integrated Claude Opus 4.5 into Ochtopus for the most advanced AI coding assistance available today.',
    image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1200&h=600&fit=crop',
    author: { name: 'Ochtopus Team', avatar: 'OT', role: 'Product Team' },
    date: 'Nov 25, 2025',
    readTime: '8 min',
    category: 'engineering'
  },
  {
    slug: 'ochtopus-git-integration',
    title: 'Mastering Git with Ochtopus: AI-Powered Version Control',
    excerpt: 'Smart commit messages, conflict resolution, and visual diffs. Learn how Ochtopus makes Git easier than ever.',
    image: 'https://images.unsplash.com/photo-1556075798-4825dfaaf498?w=1200&h=600&fit=crop',
    author: { name: 'Ochtopus Team', avatar: 'OT', role: 'Engineering Team' },
    date: 'Nov 20, 2025',
    readTime: '5 min',
    category: 'tips'
  },
  {
    slug: 'ochtopus-team-collaboration',
    title: 'How Teams Are Collaborating with Ochtopus',
    excerpt: 'Case studies from teams using Ochtopus for real-time collaboration, code reviews, and shared AI sessions.',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&h=600&fit=crop',
    author: { name: 'Ochtopus Team', avatar: 'OT', role: 'Enterprise Team' },
    date: 'Nov 15, 2025',
    readTime: '6 min',
    category: 'enterprise'
  }
];

export function BlogPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get search results for dropdown
  const searchResults = searchQuery.length > 0
    ? blogPosts.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 5)
    : [];

  // Translated categories
  const translatedCategories = [
    { id: 'all', label: t('blog.categories.all'), icon: BookOpen },
    { id: 'product', label: t('blog.categories.product'), icon: Sparkles },
    { id: 'engineering', label: t('blog.categories.engineering'), icon: TrendingUp },
    { id: 'tips', label: t('blog.categories.tips'), icon: BookOpen },
    { id: 'enterprise', label: t('blog.categories.enterprise'), icon: Users },
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = activeCategory === 'all' || post.category === activeCategory;
    const matchesSearch = searchQuery === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = filteredPosts.find(post => post.featured) || filteredPosts[0];
  const regularPosts = filteredPosts.filter(post => post !== featuredPost);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
    }
  };

  return (
    <>
      <SEO {...SEO_CONFIG.blog} />
      <div className="min-h-screen relative overflow-hidden">
      <BlogBackground />
      {/* Hero */}
      <section className="section-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />

        <div className="container relative">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm mb-6">
              <BookOpen size={16} />
              <span>{t('blog.badge')}</span>
            </div>

            <h1 className="heading-xl mb-4">
              {t('blog.title')}{' '}
              <span className="text-accent">{t('blog.titleHighlight')}</span>
            </h1>
            <p className="text-text-secondary text-lg mb-8">
              {t('blog.subtitle')}
            </p>

            {/* Search */}
            <div className="relative max-w-lg mx-auto" ref={searchRef}>
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary z-10" />
              <input
                type="text"
                placeholder={t('blog.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchDropdown(e.target.value.length > 0);
                }}
                onFocus={() => searchQuery.length > 0 && setShowSearchDropdown(true)}
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-bg-card border border-border-default focus:border-accent focus:outline-none"
              />

              {/* Search Dropdown */}
              {showSearchDropdown && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-bg-card border border-border-default rounded-xl shadow-xl overflow-hidden z-50">
                  {searchResults.map((post) => (
                    <Link
                      key={post.slug}
                      to={`/blog/${post.slug}`}
                      onClick={() => {
                        setShowSearchDropdown(false);
                        setSearchQuery('');
                      }}
                      className="flex items-start gap-4 p-4 hover:bg-bg-elevated transition-colors border-b border-border-default last:border-b-0"
                    >
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-16 h-12 object-cover rounded-lg flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm text-text-primary line-clamp-1">{post.title}</h4>
                        <p className="text-xs text-text-secondary line-clamp-1 mt-0.5">{post.excerpt}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-accent">{post.category}</span>
                          <span className="text-xs text-text-tertiary">• {post.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* No results message */}
              {showSearchDropdown && searchQuery.length > 0 && searchResults.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-bg-card border border-border-default rounded-xl shadow-xl p-4 z-50">
                  <p className="text-text-secondary text-sm text-center">Sonuç bulunamadı</p>
                </div>
              )}
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {translatedCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all',
                  activeCategory === category.id
                    ? 'bg-accent text-white'
                    : 'bg-bg-card border border-border-default text-text-secondary hover:text-text-primary hover:border-accent'
                )}
              >
                <category.icon size={16} />
                {category.label}
              </button>
            ))}
          </div>

          {/* Featured Post */}
          {featuredPost && (
            <div className="mb-16">
              <Link to={`/blog/${featuredPost.slug}`} className="block group">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  <div className="relative overflow-hidden rounded-2xl aspect-video">
                    <img
                      src={featuredPost.image}
                      alt={featuredPost.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-500 to-violet-500 text-white text-sm font-medium shadow-lg">
                        {t('blog.featured')}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent/10 text-accent text-sm font-medium mb-4">
                      <Sparkles size={14} />
                      {featuredPost.category.charAt(0).toUpperCase() + featuredPost.category.slice(1)}
                    </span>
                    <h2 className="text-2xl md:text-3xl font-bold mb-4 group-hover:text-accent transition-colors">
                      {featuredPost.title}
                    </h2>
                    <p className="text-text-secondary mb-6 text-lg">
                      {featuredPost.excerpt}
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center text-white font-semibold text-sm">
                          {featuredPost.author.avatar}
                        </div>
                        <div>
                          <span className="font-medium block text-sm">{featuredPost.author.name}</span>
                          <span className="text-text-tertiary text-xs">{featuredPost.author.role}</span>
                        </div>
                      </div>
                      <span className="text-text-tertiary flex items-center gap-1 text-sm">
                        <Calendar size={14} />
                        {featuredPost.date}
                      </span>
                      <span className="text-text-tertiary flex items-center gap-1 text-sm">
                        <Clock size={14} />
                        {featuredPost.readTime}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularPosts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group"
              >
                <article className="card h-full flex flex-col hover:border-accent/30 transition-all">
                  <div className="relative overflow-hidden rounded-xl aspect-video mb-4 -mx-6 -mt-6">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-medium mb-3 self-start">
                    {post.category.charAt(0).toUpperCase() + post.category.slice(1)}
                  </span>
                  <h3 className="text-lg font-semibold mb-2 group-hover:text-accent transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-text-secondary text-sm mb-4 flex-1 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-border-default">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center text-white text-xs font-semibold">
                        {post.author.avatar}
                      </div>
                      <span className="text-sm">{post.author.name}</span>
                    </div>
                    <span className="text-text-tertiary text-xs flex items-center gap-1">
                      <Clock size={12} />
                      {post.readTime}
                    </span>
                  </div>
                </article>
              </Link>
            ))}
          </div>

          {/* Load More */}
          <div className="text-center mt-12">
            <button className="btn btn-secondary">
              {t('blog.loadMore')}
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="section">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <div className="card p-8 md:p-12 text-center bg-gradient-to-br from-accent/10 to-purple-500/5 border-accent/20">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center mx-auto mb-6">
                <Bell size={28} className="text-white" />
              </div>
              <h2 className="heading-lg mb-3">{t('blog.newsletter.title')}</h2>
              <p className="text-text-secondary mb-8">
                {t('blog.newsletter.description')}
              </p>

              {subscribed ? (
                <div className="flex items-center justify-center gap-2 text-green-400">
                  <CheckCircle2 size={24} />
                  <span className="text-lg font-medium">{t('blog.newsletter.success')}</span>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <input
                    type="email"
                    placeholder={t('blog.newsletter.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="flex-1 px-4 py-3 rounded-xl bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                  <button type="submit" className="btn btn-primary">
                    {t('blog.newsletter.subscribe')}
                  </button>
                </form>
              )}

              <p className="text-text-tertiary text-sm mt-4">
                {t('blog.newsletter.disclaimer')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
